import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { campaignsApi, type Campaign } from '../campaignsApi';
import { apiFetch, getAuthHeaders } from '../../../services/http';
import { useSessionStore } from '../../../stores/sessionStore';
import './campaign-origin-picker.css';
import { campaignOriginOptions, campaignOriginSummary } from '../campaignOriginOptions';

export function CampaignOriginPicker({ campaign, onClose }: { campaign: Campaign; onClose: () => void }) {
  const token = useSessionStore(state => state.session?.access_token);
  const tenantId = useSessionStore(state => state.activeTenantId);
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState('');
  const [remoteId, setRemoteId] = useState('');
  const headers = getAuthHeaders(token!);
  const accounts = useQuery({ queryKey: ['meta-accounts', tenantId],
    queryFn: () => campaignsApi.getMetaAccounts({ headers }), enabled: Boolean(token) });
  const remote = useQuery({ queryKey: ['meta-remote-campaigns', tenantId, accountId],
    queryFn: () => campaignsApi.listRemoteMetaCampaigns(accountId, { headers }), enabled: Boolean(accountId && token),
    retry: false, refetchOnMount: 'always' });
  const options = campaignOriginOptions(remote.data?.items || []);
  const available = options.filter(option => !option.disabled);
  const mutation = useMutation({
    mutationFn: () => apiFetch(`/v1/crm/campaigns/${encodeURIComponent(campaign.id)}/origin/meta`, {
      method: 'POST', headers, body: JSON.stringify({ adAccountId: accountId, remoteCampaignId: remoteId }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      queryClient.invalidateQueries({ queryKey: ['meta-remote-campaigns'] });
      onClose();
    },
  });
  const error = mutation.error || accounts.error || remote.error;
  const canLink = available.some(option => option.id === remoteId) && !remote.isFetching && !remote.isError;
  return <div className="campaign-origin-picker" role="dialog" aria-label={`Vincular origem de ${campaign.name}`}>
    <strong>Vincular à campanha Meta existente</strong>
    <label>Conta de anúncios<select value={accountId} disabled={mutation.isPending} onChange={event => {
      setAccountId(event.target.value); setRemoteId('');
    }}><option value="">Selecione a conta</option>{accounts.data?.accounts.map(account =>
      <option key={account.adAccountId} value={account.adAccountId}>{account.name} ({account.adAccountId})</option>)}</select></label>
    <label>Campanha na origem<select value={remoteId} disabled={!accountId || remote.isFetching || mutation.isPending || remote.isError}
      onChange={event => setRemoteId(event.target.value)}><option value="">{remote.isFetching ? 'Consultando campanhas na Meta...' : 'Selecione a campanha'}</option>
      {options.map(option => <option key={option.id} value={option.id} disabled={option.disabled}>{option.label}</option>)}</select></label>
    {accountId && <button type="button" className="campaign-origin-refresh" disabled={remote.isFetching || mutation.isPending}
      onClick={() => { setRemoteId(''); remote.refetch(); }}>{remote.isFetching ? 'Consultando...' : 'Atualizar campanhas da Meta'}</button>}
    {remote.isSuccess && !remote.isFetching && <small role="status">{campaignOriginSummary(options.length, available.length)}</small>}
    {remote.isFetching && <small role="status">Consultando a conta selecionada. Aguarde o retorno da Meta.</small>}
    <small>O status local será atualizado conforme a origem. Nenhum anúncio será criado ou ativado.</small>
    {error && <small role="alert">{error.message}</small>}
    <div className="campaign-origin-actions"><button type="button" disabled={mutation.isPending} onClick={onClose}>Cancelar</button>
      <button type="button" disabled={!canLink || mutation.isPending} onClick={() => mutation.mutate()}>
        {mutation.isPending ? 'Validando...' : 'Vincular origem'}</button></div>
  </div>;
}
